<template>
  <div class="screen-bill-admin-shell py-8 px-4">
    <v-container class="admin-container">
      <!-- Header -->
      <div class="d-flex flex-column flex-sm-row justify-space-between align-start align-sm-center mb-6">
        <div>
          <h1 class="text-h4 font-weight-black white--text italic-title">SCREENWERBUNG</h1>
          <h2 class="subtitle-1 yellow--text text--darken-2 font-weight-bold text-uppercase">Rechnungsgenerator</h2>
        </div>
        <div class="d-flex flex-wrap gap-2 mt-4 mt-sm-0">
          <v-btn color="#fbc02d" class="black--text font-weight-bold" @click="openCreateDialog" title="Auftrag erfassen">
            <v-icon :left="!$vuetify.breakpoint.xsOnly">mdi-plus</v-icon>
            <span class="hidden-xs-only">Auftrag erfassen</span>
          </v-btn>
          <v-btn color="grey darken-3" class="white--text font-weight-bold" @click="exportCSV" :loading="exporting" title="CSV Export">
            <v-icon :left="!$vuetify.breakpoint.xsOnly">mdi-file-delimited-outline</v-icon>
            <span class="hidden-xs-only">CSV Export</span>
          </v-btn>
          <v-btn text outlined color="grey lighten-1" @click="fetchData" :loading="loading" title="Aktualisieren">
            <v-icon :left="!$vuetify.breakpoint.xsOnly">mdi-refresh</v-icon>
            <span class="hidden-xs-only">Aktualisieren</span>
          </v-btn>
        </div>
      </div>

      <!-- Stats Cards -->
      <v-row class="mb-6">
        <v-col cols="12" sm="6" md="3">
          <v-card dark class="stat-card pa-4 rounded-xl">
            <div class="caption grey--text">Aufgeschaltete Werbung</div>
            <div class="text-h4 font-weight-black white--text my-1">
              {{ stats.activeCount }}
            </div>
            <div class="caption green--text mt-2 d-flex align-center">
              <v-icon color="green" x-small class="mr-1">mdi-play-circle-outline</v-icon>
              Aktuell im Sendedienst
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card dark class="stat-card pa-4 rounded-xl">
            <div class="caption grey--text">Einnahmen (Bezahlt)</div>
            <div class="text-h4 font-weight-black green--text text--accent-3 my-1">CHF {{ stats.paidVolume.toFixed(2) }}</div>
            <div class="caption grey--text mt-2">Aus beglichenen Rechnungen</div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card dark class="stat-card pa-4 rounded-xl">
            <div class="caption grey--text">Ausstehende Rechnungen</div>
            <div class="text-h4 font-weight-black orange--text my-1">
              {{ stats.pendingCount }}
            </div>
            <div class="caption grey--text mt-2">Nicht bezahlt, nicht deaktiviert</div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card dark class="stat-card pa-4 rounded-xl">
            <div class="caption grey--text">Deaktivierte Aufträge</div>
            <div class="text-h4 font-weight-black grey--text text--lighten-1 my-1">
              {{ stats.deactivatedCount }}
            </div>
            <div class="caption grey--text mt-2">Abgelaufen / Deaktiviert</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Filter Controls Card -->
      <v-card dark class="table-card rounded-xl pa-4 mb-6">
        <h3 class="subtitle-1 font-weight-bold white--text mb-3 d-flex align-center">
          <v-icon color="#fbc02d" class="mr-2">mdi-filter-variant</v-icon>
          Filter & Suche
        </h3>
        <v-row dense align="center">
          <!-- Text Search -->
          <v-col cols="12" sm="4" md="3">
            <v-text-field
              v-model="filters.search"
              append-icon="mdi-magnify"
              label="Kunde, Projekt oder Ansprechpartner..."
              single-line
              hide-details
              outlined
              dense
              class="search-field rounded-lg"
              @input="debouncedFetch"></v-text-field>
          </v-col>

          <!-- Studio Filter -->
          <v-col cols="12" sm="4" md="3">
            <v-select
              v-model="filters.studioId"
              :items="studios"
              item-text="name"
              item-value="_id"
              label="Standort filtern..."
              outlined
              dense
              hide-details
              clearable
              class="search-field rounded-lg"
              @change="fetchData"></v-select>
          </v-col>

          <!-- Date Filter Start -->
          <v-col cols="12" sm="2" md="2">
            <v-text-field
              v-model="filters.startDate"
              label="Startzeitraum"
              type="date"
              outlined
              dense
              hide-details
              class="search-field rounded-lg"
              @change="fetchData"></v-text-field>
          </v-col>

          <!-- Date Filter End -->
          <v-col cols="12" sm="2" md="2">
            <v-text-field
              v-model="filters.endDate"
              label="Endzeitraum"
              type="date"
              outlined
              dense
              hide-details
              class="search-field rounded-lg"
              @change="fetchData"></v-text-field>
          </v-col>

          <!-- Clear Filters -->
          <v-col cols="12" sm="12" md="2" class="text-right">
            <v-btn text color="grey lighten-1" @click="clearFilters" small> <v-icon left>mdi-close-circle-outline</v-icon> Zurücksetzen </v-btn>
          </v-col>
        </v-row>

        <v-divider class="grey darken-3 my-4"></v-divider>

        <!-- Status Chips Filter -->
        <v-chip-group v-model="filters.status" mandatory active-class="active-chip" @change="fetchData">
          <v-chip filter value="all" small outlined>Alle ({{ totalCount }})</v-chip>
          <v-chip filter value="active" small color="green" outlined>Aufgeschaltet ({{ countWorkflowStatus("active") }})</v-chip>
          <v-chip filter value="paid" small color="green accent-3" outlined>Bezahlt ({{ countWorkflowStatus("paid") }})</v-chip>
          <v-chip filter value="unpaid" small color="orange" outlined>Zahlung offen ({{ countWorkflowStatus("unpaid") }})</v-chip>
          <v-chip filter value="deactivated" small color="grey" outlined>Deaktiviert ({{ countWorkflowStatus("deactivated") }})</v-chip>
        </v-chip-group>
      </v-card>

      <!-- Main Table -->
      <v-card dark class="table-card rounded-xl pa-4">
        <v-data-table
          :headers="headers"
          :items="orders"
          :loading="loading"
          class="custom-table"
          :items-per-page="15"
          no-data-text="Keine Werbeaufträge gefunden."
          dark>
          <!-- Company/Customer info -->
          <template v-slot:item.company="{ item }">
            <div class="py-1">
              <div class="body-2 font-weight-bold white--text">{{ item.companyName }}</div>
              <div class="caption grey--text d-flex flex-column">
                <span v-if="item.contactPerson">Ansprechp.: {{ item.contactPerson }}</span>
                <span>{{ item.street }}, {{ item.zip }} {{ item.city }}</span>
                <span
                  v-if="item.createdBy"
                  class="mt-1 font-weight-medium text-uppercase"
                  style="font-size: 9px !important; color: #fbc02d !important">
                  Erfasst von: {{ item.createdBy.vorname }} {{ item.createdBy.nachname }}
                </span>
              </div>
            </div>
          </template>

          <!-- Campaign / Period details -->
          <template v-slot:item.project="{ item }">
            <div class="py-1">
              <div class="body-2 font-weight-bold yellow--text text--darken-2">{{ item.projectTitle }}</div>
              <div class="caption grey--text mt-1">
                <v-icon x-small color="grey" class="mr-1">mdi-calendar-range</v-icon>
                {{ formatDate(item.startDate) }} - {{ formatDate(item.endDate) }}
              </div>
            </div>
          </template>

          <!-- Price -->
          <template v-slot:item.price="{ item }">
            <div class="font-weight-bold white--text">CHF {{ item.price.toFixed(2) }}</div>
            <div class="caption grey--text" style="font-size: 9px !important">inkl. 8.1% MWST</div>
          </template>

          <!-- Selected Studios -->
          <template v-slot:item.studios="{ item }">
            <div class="d-flex flex-wrap gap-1 max-w-studios">
              <v-chip v-for="studio in item.studios" :key="studio._id" x-small outlined color="grey lighten-1" class="ma-1">
                {{ studio.name }}
              </v-chip>
              <div v-if="!item.studios || !item.studios.length" class="caption grey--text italic">Keine</div>
            </div>
          </template>

          <!-- Workflow Switches Checklist -->
          <template v-slot:item.workflow="{ item }">
            <div class="d-flex align-center justify-space-around py-1 workflow-box">
              <!-- Active status check -->
              <div class="d-flex flex-column align-center px-2">
                <span class="workflow-label">Aufgeschaltet</span>
                <v-checkbox v-model="item.isActive" color="green" hide-details dense @change="toggleWorkflowFlag(item, 'isActive')"></v-checkbox>
              </div>

              <!-- Paid status check -->
              <div class="d-flex flex-column align-center px-2">
                <span class="workflow-label">Bezahlt</span>
                <v-checkbox v-model="item.isPaid" color="green accent-3" hide-details dense @change="toggleWorkflowFlag(item, 'isPaid')"></v-checkbox>
              </div>

              <!-- Deactivated status check -->
              <div class="d-flex flex-column align-center px-2">
                <span class="workflow-label">Deaktiviert</span>
                <v-checkbox
                  v-model="item.isDeactivated"
                  color="red"
                  hide-details
                  dense
                  @change="toggleWorkflowFlag(item, 'isDeactivated')"></v-checkbox>
              </div>
            </div>
          </template>

          <!-- Zahlungszweck & Quick Edit -->
          <template v-slot:item.paymentPurpose="{ item }">
            <div class="d-flex align-center gap-2 py-1 max-w-purpose">
              <span class="caption text-truncate-purpose">{{ item.paymentPurpose }}</span>
              <v-btn icon x-small color="yellow darken-2" @click="openPurposeQuickEdit(item)">
                <v-icon small>mdi-pencil-outline</v-icon>
              </v-btn>
            </div>
          </template>

          <!-- Action operations -->
          <template v-slot:item.actions="{ item }">
            <div class="d-flex align-center justify-end">
              <!-- Comments -->
              <v-btn icon color="blue lighten-2" class="mr-1" title="Kommentare & Notizen" @click="openCommentsDialog(item)">
                <v-badge
                  color="red"
                  :content="item.comments ? item.comments.length : 0"
                  :value="item.comments && item.comments.length > 0"
                  overlap
                  bordered
                  dot-class="caption">
                  <v-icon>mdi-comment-outline</v-icon>
                </v-badge>
              </v-btn>

              <!-- Export/Print PDF -->
              <v-btn icon color="green" class="mr-1" title="PDF Rechnungsbeleg herunterladen" @click="downloadPDF(item)">
                <v-icon>mdi-file-pdf-box</v-icon>
              </v-btn>

              <v-menu offset-y dark>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list dense bg-color="#1c1c1e">
                  <!-- Edit Details -->
                  <v-list-item @click="openEditDialog(item)">
                    <v-list-item-icon class="mr-2">
                      <v-icon color="yellow darken-2" small>mdi-pencil</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Bearbeiten</v-list-item-title>
                  </v-list-item>

                  <v-divider class="grey darken-3"></v-divider>

                  <!-- Delete Item -->
                  <v-list-item @click="confirmDelete(item)">
                    <v-list-item-icon class="mr-2">
                      <v-icon color="red" small>mdi-trash-can-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title class="red--text">Löschen</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </v-container>

    <!-- Create & Edit Campaign Dialog -->
    <v-dialog v-model="editDialog.show" max-width="700px" persistent>
      <v-card dark class="pa-4 rounded-xl border-highlight" style="background-color: #1c1c1e; border: 1px solid rgba(255, 255, 255, 0.08)">
        <div class="d-flex align-center justify-space-between mb-4">
          <h2 class="text-h5 font-weight-black white--text italic-title">
            <v-icon color="#fbc02d" class="mr-2">
              {{ editDialog.isNew ? "mdi-plus-box-outline" : "mdi-square-edit-outline" }}
            </v-icon>
            {{ editDialog.isNew ? "Neuen Werbeauftrag erfassen" : "Werbeauftrag bearbeiten" }}
          </h2>
          <v-btn icon color="grey" @click="editDialog.show = false" :disabled="editDialog.saving">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-form ref="orderForm" v-model="editDialog.valid" lazy-validation>
          <!-- Customer Address Block -->
          <h3 class="subtitle-2 font-weight-black yellow--text text--darken-2 text-uppercase mb-3">
            <v-icon color="yellow darken-2" x-small class="mr-1">mdi-domain</v-icon>
            Kundendaten (Rechnungsadresse)
          </h3>
          <v-row dense>
            <v-col cols="12" sm="8">
              <v-text-field
                v-model="editDialog.form.companyName"
                label="Firmenname *"
                outlined
                dense
                :rules="[(v) => !!v || 'Firmenname erforderlich']"
                class="rounded-lg search-field"></v-text-field>
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="editDialog.form.contactPerson"
                label="Ansprechpartner (optional)"
                outlined
                dense
                class="rounded-lg search-field"></v-text-field>
            </v-col>
            <v-col cols="12" sm="12">
              <v-text-field
                v-model="editDialog.form.street"
                label="Strasse / Hausnummer *"
                outlined
                dense
                :rules="[(v) => !!v || 'Strasse erforderlich']"
                class="rounded-lg search-field"></v-text-field>
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="editDialog.form.zip"
                label="PLZ *"
                outlined
                dense
                :rules="[(v) => !!v || 'PLZ erforderlich']"
                class="rounded-lg search-field"></v-text-field>
            </v-col>
            <v-col cols="12" sm="8">
              <v-text-field
                v-model="editDialog.form.city"
                label="Ort *"
                outlined
                dense
                :rules="[(v) => !!v || 'Ort erforderlich']"
                class="rounded-lg search-field"></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="editDialog.form.email"
                label="E-Mail (optional)"
                type="email"
                outlined
                dense
                class="rounded-lg search-field"></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="editDialog.form.phone"
                label="Telefonnummer (optional)"
                outlined
                dense
                class="rounded-lg search-field"></v-text-field>
            </v-col>
          </v-row>

          <v-divider class="grey darken-3 my-4"></v-divider>

          <!-- Campaign / Billing Block -->
          <h3 class="subtitle-2 font-weight-black yellow--text text--darken-2 text-uppercase mb-3">
            <v-icon color="yellow darken-2" x-small class="mr-1">mdi-television-classic</v-icon>
            Werbekampagne & Preiskalkulation
          </h3>
          <v-row dense>
            <v-col cols="12" sm="8">
              <v-text-field
                v-model="editDialog.form.projectTitle"
                label="Projekt-/Kampagnenname *"
                outlined
                dense
                :rules="[(v) => !!v || 'Projektname erforderlich']"
                class="rounded-lg search-field"></v-text-field>
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model.number="editDialog.form.price"
                label="Rechnungsbetrag (CHF) *"
                type="number"
                outlined
                dense
                min="0"
                step="0.01"
                prefix="CHF"
                :rules="[(v) => (v !== undefined && v !== '' && v >= 0) || 'Betrag muss 0 oder höher sein']"
                class="rounded-lg search-field"></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="editDialog.form.startDate"
                label="Schaltung von *"
                type="date"
                outlined
                dense
                :rules="[(v) => !!v || 'Startdatum erforderlich']"
                class="rounded-lg search-field"></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="editDialog.form.endDate"
                label="Schaltung bis *"
                type="date"
                outlined
                dense
                :rules="[(v) => !!v || 'Enddatum erforderlich']"
                class="rounded-lg search-field"></v-text-field>
            </v-col>

            <!-- Location Checklist multi-select -->
            <v-col cols="12">
              <span class="caption grey--text mb-2 d-block">Standorte auswählen (Studios) *</span>
              <div class="d-flex flex-wrap gap-2 studio-checkboxes-container pa-3 rounded-lg">
                <v-checkbox
                  v-for="studio in studios"
                  :key="studio._id"
                  v-model="editDialog.form.studios"
                  :value="studio._id"
                  :label="studio.name"
                  color="#fbc02d"
                  hide-details
                  class="ma-2 studio-cb-item"></v-checkbox>
              </div>
              <div v-if="!editDialog.form.studios.length" class="caption red--text mt-1">Bitte wählen Sie mindestens einen Standort aus.</div>
            </v-col>

            <v-col cols="12" class="mt-2">
              <v-textarea
                v-model="editDialog.form.description"
                label="Projektbeschrieb / Detailinformationen (optional)"
                outlined
                dense
                rows="3"
                class="rounded-lg search-field"></v-textarea>
            </v-col>

            <!-- Editable Zahlungszweck (Only for updates or if they want to override manually) -->
            <v-col cols="12" v-if="!editDialog.isNew">
              <v-text-field
                v-model="editDialog.form.paymentPurpose"
                label="Zahlungszweck (Swiss QR-Bill Unstructured Message)"
                outlined
                dense
                class="rounded-lg search-field"
                placeholder="Automatisch generiert aus Zeitraum + Projektname"></v-text-field>
            </v-col>
          </v-row>
        </v-form>

        <div class="d-flex justify-end gap-2 mt-6">
          <v-btn text color="grey" @click="editDialog.show = false" :disabled="editDialog.saving"> Abbrechen </v-btn>
          <v-btn
            color="#fbc02d"
            class="black--text font-weight-black rounded-lg"
            :loading="editDialog.saving"
            :disabled="!editDialog.valid || !editDialog.form.studios.length"
            @click="submitForm">
            {{ editDialog.isNew ? "Auftrag erstellen" : "Speichern" }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Quick Edit Zahlungszweck Dialog -->
    <v-dialog v-model="purposeDialog.show" max-width="500px">
      <v-card dark class="pa-4 rounded-xl border-highlight" style="background-color: #1c1c1e; border: 1px solid rgba(255, 255, 255, 0.08)">
        <div class="d-flex align-center justify-space-between mb-4">
          <h2 class="text-h6 font-weight-black white--text italic-title">Zahlungszweck anpassen</h2>
          <v-btn icon color="grey" @click="purposeDialog.show = false" :disabled="purposeDialog.saving">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="caption grey--text mb-4">
          Passen Sie den Zahlungszweck an, der als unstrukturierte Mitteilung auf dem Schweizer QR-Beleg gedruckt wird.
        </div>

        <v-text-field v-model="purposeDialog.paymentPurpose" label="Zahlungszweck" outlined dense class="rounded-lg search-field"></v-text-field>

        <div class="d-flex justify-end gap-2 mt-4">
          <v-btn text color="grey" @click="purposeDialog.show = false" :disabled="purposeDialog.saving"> Abbrechen </v-btn>
          <v-btn color="#fbc02d" class="black--text font-weight-black rounded-lg" :loading="purposeDialog.saving" @click="submitPurposeQuickEdit">
            Übernehmen
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Deletion Confirmation Dialog -->
    <v-dialog v-model="deleteDialog.show" max-width="400px">
      <v-card dark class="pa-4 rounded-xl" style="background-color: #1c1c1e; border: 1px solid rgba(255, 255, 255, 0.08)">
        <h2 class="text-h6 font-weight-black white--text mb-3">Werbeauftrag löschen?</h2>
        <p class="body-2 grey--text">
          Sind Sie sicher, dass Sie den Werbeauftrag für <strong>{{ deleteDialog.companyName }}</strong> ({{ deleteDialog.projectTitle }}) dauerhaft
          löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
        </p>
        <div class="d-flex justify-end gap-2 mt-4">
          <v-btn text color="grey" @click="deleteDialog.show = false" :disabled="deleteDialog.saving"> Abbrechen </v-btn>
          <v-btn color="red" class="white--text font-weight-bold rounded-lg" :loading="deleteDialog.saving" @click="deleteOrder">
            Dauerhaft löschen
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Comments Dialog -->
    <v-dialog v-model="commentsDialog.show" max-width="500px">
      <v-card dark class="pa-4 rounded-xl border-highlight" style="background-color: #1c1c1e; border: 1px solid rgba(255, 255, 255, 0.08)">
        <div class="d-flex align-center justify-space-between mb-4">
          <h2 class="text-h6 font-weight-black white--text italic-title">
            <v-icon color="blue" class="mr-2">mdi-comment-text-multiple-outline</v-icon>
            Kommentare & Notizen
          </h2>
          <v-btn icon color="grey" @click="commentsDialog.show = false" :disabled="commentsDialog.saving">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="caption grey--text mb-4">
          Projekt: <strong class="white--text">{{ commentsDialog.projectTitle }}</strong> ({{ commentsDialog.companyName }})
        </div>

        <!-- Comments List -->
        <div
          class="comments-list-container pa-3 mb-4 rounded-lg"
          style="background-color: rgba(255, 255, 255, 0.02); max-height: 300px; overflow-y: auto">
          <div v-for="(comment, index) in commentsDialog.comments" :key="index" class="mb-3 pb-2 border-bottom-dashed">
            <div class="body-2 white--text mb-1" style="white-space: pre-line">{{ comment.text }}</div>
            <div class="d-flex justify-space-between align-center" style="font-size: 10px; color: #777777">
              <span>{{ comment.user ? `${comment.user.vorname} ${comment.user.nachname}` : "System" }}</span>
              <span>{{ formatDateTime(comment.createdAt) }}</span>
            </div>
          </div>
          <div v-if="!commentsDialog.comments || !commentsDialog.comments.length" class="text-center py-6 caption grey--text italic">
            Keine Kommentare vorhanden. Erfassen Sie den ersten Kommentar unten.
          </div>
        </div>

        <!-- Add Comment form -->
        <v-textarea
          v-model="commentsDialog.newText"
          label="Neuer Kommentar..."
          outlined
          dense
          rows="2"
          hide-details
          class="rounded-lg search-field mb-3"></v-textarea>

        <div class="d-flex justify-end gap-2">
          <v-btn text color="grey" @click="commentsDialog.show = false" :disabled="commentsDialog.saving"> Schliessen </v-btn>
          <v-btn
            color="#fbc02d"
            class="black--text font-weight-black rounded-lg"
            :loading="commentsDialog.saving"
            :disabled="!commentsDialog.newText || !commentsDialog.newText.trim()"
            @click="submitComment">
            Kommentar senden
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"

export default {
  name: "ScreenBillAdmin",
  data() {
    return {
      loading: false,
      exporting: false,
      totalCount: 0,

      // Filter inputs
      filters: {
        search: "",
        status: "all",
        studioId: null,
        startDate: "",
        endDate: "",
      },
      debounceTimer: null,

      // Database lists
      orders: [],
      studios: [],

      // Stats counters
      stats: {
        activeCount: 0,
        paidVolume: 0,
        pendingCount: 0,
        deactivatedCount: 0,
      },

      // Add/Edit Dialog data
      editDialog: {
        show: false,
        isNew: true,
        valid: true,
        saving: false,
        orderId: null,
        form: {
          companyName: "",
          contactPerson: "",
          street: "",
          zip: "",
          city: "",
          email: "",
          phone: "",
          startDate: "",
          endDate: "",
          projectTitle: "",
          description: "",
          price: 0,
          studios: [],
          paymentPurpose: "",
        },
      },

      // Quick Purpose Dialog data
      purposeDialog: {
        show: false,
        saving: false,
        orderId: null,
        paymentPurpose: "",
      },

      // Deletion Dialog data
      deleteDialog: {
        show: false,
        saving: false,
        orderId: null,
        companyName: "",
        projectTitle: "",
      },

      // Comments Dialog data
      commentsDialog: {
        show: false,
        saving: false,
        orderId: null,
        companyName: "",
        projectTitle: "",
        comments: [],
        newText: "",
      },

      // Table headers
      headers: [
        { text: "Kunde / Rechnungsadresse", value: "company", sortable: false },
        { text: "Projekt & Zeitraum", value: "project", sortable: false },
        { text: "Preis", value: "price", align: "right", width: "120px" },
        { text: "Standorte", value: "studios", sortable: false, width: "180px" },
        { text: "Workflow-Schritte", value: "workflow", align: "center", sortable: false, width: "240px" },
        { text: "Zahlungszweck (QR)", value: "paymentPurpose", sortable: false, width: "200px" },
        { text: "", value: "actions", sortable: false, align: "right", width: "100px" },
      ],
    }
  },
  async created() {
    this.loading = true
    await this.fetchStudios()
    await this.fetchData()
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return ""
      const d = new Date(dateStr)
      if (isNaN(d.getTime())) return ""
      return d.toLocaleDateString("de-CH", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    },

    formatInputDate(dateStr) {
      if (!dateStr) return ""
      const d = new Date(dateStr)
      if (isNaN(d.getTime())) return ""
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, "0")
      const day = String(d.getDate()).padStart(2, "0")
      return `${year}-${month}-${day}`
    },

    debouncedFetch() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.fetchData()
      }, 350)
    },

    clearFilters() {
      this.filters = {
        search: "",
        status: "all",
        studioId: null,
        startDate: "",
        endDate: "",
      }
      this.fetchData()
    },

    countWorkflowStatus(status) {
      if (status === "active") {
        return this.orders.filter((o) => o.isActive && !o.isDeactivated).length
      } else if (status === "paid") {
        return this.orders.filter((o) => o.isPaid).length
      } else if (status === "unpaid") {
        return this.orders.filter((o) => !o.isPaid && !o.isDeactivated).length
      } else if (status === "deactivated") {
        return this.orders.filter((o) => o.isDeactivated).length
      }
      return 0
    },

    async fetchStudios() {
      try {
        const res = await api.screenBill.admin.fetchStudios()
        this.studios = res || []
      } catch (err) {
        console.error(err)
        this.$toast.error("Standorte konnten nicht geladen werden.")
      }
    },

    async fetchData() {
      this.loading = true
      try {
        const params = {
          search: this.filters.search,
          studioId: this.filters.studioId,
          startDate: this.filters.startDate,
          endDate: this.filters.endDate,
        }

        if (this.filters.status !== "all") {
          params.status = this.filters.status
        }

        const res = await api.screenBill.admin.fetchOrders(params)
        this.orders = res || []
        this.totalCount = this.orders.length
        this.recalculateStats()
      } catch (err) {
        console.error(err)
        this.$toast.error("Fehler beim Laden der Werbeaufträge.")
      } finally {
        this.loading = false
      }
    },

    recalculateStats() {
      let activeCount = 0
      let paidVolume = 0
      let pendingCount = 0
      let deactivatedCount = 0

      this.orders.forEach((o) => {
        // Count revenue for any paid order, active or deactivated
        if (o.isPaid) {
          paidVolume += o.price
        }

        // Count active screen campaigns
        if (o.isActive && !o.isDeactivated) {
          activeCount++
        }

        // Count deactivated orders
        if (o.isDeactivated) {
          deactivatedCount++
        }

        // Count pending unpaid orders (neither paid nor deactivated)
        if (!o.isPaid && !o.isDeactivated) {
          pendingCount++
        }
      })

      this.stats = {
        activeCount,
        paidVolume,
        pendingCount,
        deactivatedCount,
      }
    },

    openCreateDialog() {
      this.editDialog = {
        show: true,
        isNew: true,
        valid: true,
        saving: false,
        orderId: null,
        form: {
          companyName: "",
          contactPerson: "",
          street: "",
          zip: "",
          city: "",
          email: "",
          phone: "",
          startDate: this.formatInputDate(new Date()),
          endDate: this.formatInputDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)), // 30 days default
          projectTitle: "",
          description: "",
          price: 0,
          studios: [],
          paymentPurpose: "",
        },
      }
      this.$nextTick(() => {
        if (this.$refs.orderForm) this.$refs.orderForm.resetValidation()
      })
    },

    openEditDialog(item) {
      this.editDialog = {
        show: true,
        isNew: false,
        valid: true,
        saving: false,
        orderId: item._id,
        form: {
          companyName: item.companyName,
          contactPerson: item.contactPerson || "",
          street: item.street,
          zip: item.zip,
          city: item.city,
          email: item.email || "",
          phone: item.phone || "",
          startDate: this.formatInputDate(item.startDate),
          endDate: this.formatInputDate(item.endDate),
          projectTitle: item.projectTitle,
          description: item.description || "",
          price: item.price,
          studios: (item.studios || []).map((s) => s._id || s),
          paymentPurpose: item.paymentPurpose || "",
        },
      }
      this.$nextTick(() => {
        if (this.$refs.orderForm) this.$refs.orderForm.resetValidation()
      })
    },

    async submitForm() {
      if (this.$refs.orderForm && !this.$refs.orderForm.validate()) {
        return
      }

      this.editDialog.saving = true
      try {
        if (this.editDialog.isNew) {
          await api.screenBill.admin.createOrder(this.editDialog.form)
          this.$toast.success("Werbeauftrag erfolgreich erfasst.")
        } else {
          await api.screenBill.admin.updateOrder(this.editDialog.orderId, this.editDialog.form)
          this.$toast.success("Werbeauftrag erfolgreich aktualisiert.")
        }
        this.editDialog.show = false
        await this.fetchData()
      } catch (err) {
        console.error(err)
        this.$toast.error(err.response?.data || "Speichern fehlgeschlagen.")
      } finally {
        this.editDialog.saving = false
      }
    },

    async toggleWorkflowFlag(item, flagName) {
      try {
        const payload = {}
        payload[flagName] = item[flagName]

        // Automatisches Anpassen der anderen Flags für intuitive Bedienung:
        // z.B. wenn deaktiviert wird, wird active ausgeschaltet (oder umgekehrt)
        if (flagName === "isDeactivated" && item.isDeactivated) {
          item.isActive = false
          payload.isActive = false
        } else if (flagName === "isActive" && item.isActive) {
          item.isDeactivated = false
          payload.isDeactivated = false
        }

        await api.screenBill.admin.updateOrder(item._id, payload)
        this.$toast.success("Workflow-Status aktualisiert.")
        this.recalculateStats()
      } catch (err) {
        console.error(err)
        this.$toast.error("Statusaktualisierung fehlgeschlagen.")
        // Rollback state visually
        item[flagName] = !item[flagName]
      }
    },

    openPurposeQuickEdit(item) {
      this.purposeDialog = {
        show: true,
        saving: false,
        orderId: item._id,
        paymentPurpose: item.paymentPurpose || "",
      }
    },

    async submitPurposeQuickEdit() {
      this.purposeDialog.saving = true
      try {
        await api.screenBill.admin.updateOrder(this.purposeDialog.orderId, {
          paymentPurpose: this.purposeDialog.paymentPurpose,
        })
        this.$toast.success("Zahlungszweck erfolgreich geändert.")
        this.purposeDialog.show = false
        await this.fetchData()
      } catch (err) {
        console.error(err)
        this.$toast.error("Ändern des Zahlungszwecks fehlgeschlagen.")
      } finally {
        this.purposeDialog.saving = false
      }
    },

    confirmDelete(item) {
      this.deleteDialog = {
        show: true,
        saving: false,
        orderId: item._id,
        companyName: item.companyName,
        projectTitle: item.projectTitle,
      }
    },

    async deleteOrder() {
      this.deleteDialog.saving = true
      try {
        await api.screenBill.admin.deleteOrder(this.deleteDialog.orderId)
        this.$toast.success("Werbeauftrag erfolgreich gelöscht.")
        this.deleteDialog.show = false
        await this.fetchData()
      } catch (err) {
        console.error(err)
        this.$toast.error("Löschen fehlgeschlagen.")
      } finally {
        this.deleteDialog.saving = false
      }
    },

    downloadPDF(item) {
      try {
        const filename = `Rechnung_${item.companyName.replace(/\s+/g, "_")}.pdf`
        this.$toast.info("PDF wird generiert...")
        api.screenBill.admin.downloadPDF(item._id, filename).catch((err) => {
          console.error(err)
          this.$toast.error("Fehler beim Herunterladen des PDF-Belegs.")
        })
      } catch (error) {
        console.error(error)
        this.$toast.error("PDF-Export fehlgeschlagen.")
      }
    },

    exportCSV() {
      this.exporting = true
      try {
        api.screenBill.admin
          .downloadCSV("screen_bill_auftraege.csv")
          .then(() => {
            this.$toast.success("CSV Export erfolgreich.")
          })
          .catch((err) => {
            console.error(err)
            this.$toast.error("CSV Export fehlgeschlagen.")
          })
          .finally(() => {
            this.exporting = false
          })
      } catch (error) {
        this.exporting = false
        console.error(error)
        this.$toast.error("CSV Export fehlgeschlagen.")
      }
    },

    openCommentsDialog(item) {
      this.commentsDialog = {
        show: true,
        saving: false,
        orderId: item._id,
        companyName: item.companyName,
        projectTitle: item.projectTitle,
        comments: item.comments || [],
        newText: "",
      }
    },

    async submitComment() {
      if (!this.commentsDialog.newText || !this.commentsDialog.newText.trim()) return

      this.commentsDialog.saving = true
      try {
        const updatedOrder = await api.screenBill.admin.addComment(this.commentsDialog.orderId, this.commentsDialog.newText)
        this.$toast.success("Kommentar erfolgreich hinzugefügt.")

        this.commentsDialog.comments = updatedOrder.comments || []
        this.commentsDialog.newText = ""

        const idx = this.orders.findIndex((o) => o._id === this.commentsDialog.orderId)
        if (idx !== -1) {
          this.$set(this.orders, idx, updatedOrder)
        }
      } catch (err) {
        console.error(err)
        this.$toast.error("Kommentar konnte nicht gespeichert werden.")
      } finally {
        this.commentsDialog.saving = false
      }
    },

    formatDateTime(dateStr) {
      if (!dateStr) return ""
      const d = new Date(dateStr)
      if (isNaN(d.getTime())) return ""
      return d.toLocaleString("de-CH", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    },
  },
}
</script>

<style scoped>
.screen-bill-admin-shell {
  background-color: #0f0f0f;
  min-height: 100vh;
  color: white;
}

.admin-container {
  max-width: 1300px;
  margin: 0 auto;
}

.italic-title {
  font-style: italic;
  font-weight: 950;
  letter-spacing: -1px;
}

/* Cards */
.stat-card {
  background-color: #1c1c1e !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3) !important;
}

/* Table Card */
.table-card {
  background-color: #1c1c1e !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}

.search-field :deep(.v-input__control) {
  border-radius: 8px !important;
}

.active-chip {
  background-color: rgba(251, 192, 45, 0.1) !important;
  color: #fbc02d !important;
  border-color: #fbc02d !important;
}

/* Custom Table Styles */
.custom-table :deep(table) {
  background-color: transparent !important;
}

.custom-table :deep(thead th) {
  font-weight: bold !important;
  text-transform: uppercase;
  font-size: 11px !important;
  letter-spacing: 0.5px;
  color: #777777 !important;
  border-bottom: 2px solid rgba(255, 255, 255, 0.08) !important;
}

.custom-table :deep(tbody td) {
  border-bottom: 1px solid rgba(255, 255, 255, 0.04) !important;
}

.custom-table :deep(tbody tr:hover) {
  background-color: rgba(255, 255, 255, 0.02) !important;
}

/* Checkbox alignment in workflow column */
.workflow-box {
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.workflow-label {
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #777777;
  font-weight: bold;
}

.max-w-studios {
  max-width: 180px;
}

.max-w-purpose {
  max-width: 200px;
}

.text-truncate-purpose {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 160px;
  display: inline-block;
}

/* Form checkboxes */
.studio-checkboxes-container {
  background-color: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  max-height: 150px;
  overflow-y: auto;
}

.studio-cb-item {
  width: 40%;
  min-width: 120px;
}

.gap-1 {
  gap: 4px;
}

.gap-2 {
  gap: 8px;
}

.gap-3 {
  gap: 12px;
}

.border-bottom-dashed {
  border-bottom: 1px dashed rgba(255, 255, 255, 0.08);
}

.comments-list-container::-webkit-scrollbar {
  width: 6px;
}
.comments-list-container::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}
</style>
